import classnames from 'classnames';
import React from 'react';
import ReactPlayer from 'react-player';

import { appConfig } from './config';
import { FixedAspectRatioBox } from './FixedAspectRatioBox';
import { Div } from './HTMLElement';
import { useBreakpoint } from './useBreakpoint';

export interface VideoProps {
  ariaLabel?: string;
  backgroundColor?: string;
  className?: string;
  options?: Options;
  variants: {
    lg?: VideoVariant;
    md?: VideoVariant;
    sm?: VideoVariant;
    xl?: VideoVariant;
    xs: VideoVariant;
    xxl?: VideoVariant;
  };
}

export interface VideoVariant {
  height: number;
  source: {
    bcom?: {
      file: string;
    };
    external?: {
      file?: string;
    };
    vimeo?: {
      id: string;
      privacyHash?: string;
      title?: string;
    };
    youtube?: {
      id: string;
      noCookie?: boolean;
    };
  };
  thumbnail?: string;
  width: number;
}

interface Options {
  autoplay?: boolean;
  controls?: boolean;
  keyboard?: boolean;
  light?: boolean;
  loop?: boolean;
  muted?: boolean;
  pip?: boolean;
  playsinline?: boolean;
  transcript?: boolean;
}

export const Video = ({ ariaLabel, backgroundColor, className, options, variants }: VideoProps) => {
  const breakpoint = useBreakpoint();

  const {
    autoplay = false,
    controls = true,
    keyboard = true,
    light = false,
    loop = false,
    muted = false,
    pip = false,
    playsinline = true,
    transcript = true,
  } = options ?? {};
  const [isPlaying, setIsPlaying] = React.useState(autoplay);

  const variant = React.useMemo(() => {
    let _variant: VideoVariant = variants.xs;

    if (['sm', 'md', 'lg', 'xl', 'xxl'].includes(breakpoint)) {
      _variant = variants.sm ? variants.sm : _variant;
    }
    if (['md', 'lg', 'xl', 'xxl'].includes(breakpoint)) {
      _variant = variants.md ? variants.md : _variant;
    }
    if (['lg', 'xl', 'xxl'].includes(breakpoint)) {
      _variant = variants.lg ? variants.lg : _variant;
    }
    if (['xl', 'xxl'].includes(breakpoint)) {
      _variant = variants.xl ? variants.xl : _variant;
    }
    if (breakpoint === 'xxl') {
      _variant = variants.xxl ? variants.xxl : _variant;
    }

    return _variant;
  }, [breakpoint, variants.lg, variants.md, variants.sm, variants.xl, variants.xs, variants.xxl]);

  const url = React.useMemo(() => getVideoUrl(variant), [variant]);

  const onClickPreview = React.useCallback(() => setIsPlaying(true), []);
  const isInteractive = controls || keyboard;
  return (
    <Div
      className={classnames(
        { 'video-portrait': variant.height > variant.width },
        { 'video-landscape': variant.width > variant.height },
        className,
      )}
    >
      {url && (
        <FixedAspectRatioBox aspectRatio={variant.width / variant.height} backgroundColor={backgroundColor}>
          <ReactPlayer
            config={{
              vimeo: variant.source.vimeo
                ? {
                    playerOptions: {
                      keyboard: keyboard,
                      transcript: transcript,
                    },
                    title: variant.source.vimeo.title,
                  }
                : undefined,
            }}
            controls={controls}
            height="100%"
            light={variant.thumbnail ?? light}
            loop={loop}
            muted={muted}
            onClickPreview={onClickPreview}
            pip={pip}
            playIcon={appConfig.video?.PlayIcon ? <appConfig.video.PlayIcon /> : undefined}
            playing={isPlaying}
            playsinline={playsinline}
            url={url}
            // style={}
            // volume={}
            width="100%"
            {...(ariaLabel
              ? { 'aria-label': ariaLabel, role: 'group' }
              : isInteractive
                ? {}
                : { 'aria-hidden': true, tabIndex: -1 })}
          />
        </FixedAspectRatioBox>
      )}
    </Div>
  );
};

export const getVideoUrl = (variant: VideoVariant) => {
  if (variant.source.bcom) {
    return variant.source.bcom.file;
  } else if (variant.source.external) {
    return variant.source.external.file;
  } else if (variant.source.youtube) {
    let _url = undefined;
    if (variant.source.youtube.noCookie) {
      _url = new URL(`https://www.youtube-nocookie.com/embed/${variant.source.youtube.id}`);
    } else {
      _url = new URL('https://www.youtube.com/watch');
      _url.searchParams.set('v', variant.source.youtube.id);
    }
    return _url.toString();
  } else if (variant.source.vimeo) {
    const _url = new URL(`https://player.vimeo.com/video/${variant.source.vimeo.id}`);
    variant.source.vimeo.privacyHash && _url.searchParams.set('h', variant.source.vimeo.privacyHash);
    return _url.toString();
  }
};
